/**
 * Reconcile the rendered "Sources" list with the citation markers the model
 * actually used inline.
 *
 * The app attaches every retrieved search result as a Citation, but the model
 * only cites a subset inline (e.g. `[Web 3]`, `[Web 4]`). Rendering all of
 * them as a flat `[1]..[N]` list (a) shows uncited sources and (b) doesn't
 * match the inline markers — and with mixed source types the flat index
 * diverges from the per-source `[Web N]` / `[M365 N]` / `[Doc N]` labels
 * entirely.
 *
 * These helpers derive each citation's true inline label (matching the
 * context builders in webSearchFactory / graphService / azureAISearchService),
 * then keep only the citations whose label appears in the answer.
 */
import type { Citation } from '@/types/chat.types';

type Bucket = 'Web' | 'Doc' | 'M365';

// Matches the inline markers emitted by the context builders. Case-insensitive
// so a model that lowercases ("[web 3]") is still reconciled.
const MARKER_PATTERN = /\[(Web|Doc|M365)\s+(\d+)\]/gi;

function bucketFor(citation: Citation): Bucket {
  const type = (citation.documentType || '').toLowerCase();
  if (type === 'web') return 'Web';
  if (type === 'document') return 'Doc'; // Azure AI Search
  return 'M365'; // Graph docs carry a mimeType (or 'unknown')
}

/**
 * Assign each citation its per-source label ("Web 3", "Doc 1", "M365 2"),
 * numbering within its own source group in array order — exactly how the
 * context builders number the inline markers.
 */
export function labelCitations(
  citations: Citation[]
): Array<{ citation: Citation; label: string }> {
  const counters: Record<Bucket, number> = { Web: 0, Doc: 0, M365: 0 };
  return citations.map((citation) => {
    const bucket = bucketFor(citation);
    counters[bucket] += 1;
    return { citation, label: `${bucket} ${counters[bucket]}` };
  });
}

/** Collect the set of citation labels actually referenced in the answer text. */
export function findCitedLabels(content: string): Set<string> {
  const labels = new Set<string>();
  const re = new RegExp(MARKER_PATTERN.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = re.exec(content)) !== null) {
    const raw = match[1].toLowerCase();
    const bucket: Bucket = raw === 'web' ? 'Web' : raw === 'doc' ? 'Doc' : 'M365';
    labels.add(`${bucket} ${match[2]}`);
  }
  return labels;
}

/**
 * The citations to actually render, paired with their inline label.
 *
 * Keeps only the citations cited inline. Falls back to showing all when the
 * answer contains no markers at all (model didn't cite inline — better to
 * show every source than none) or when markers are present but match nothing
 * we hold (defensive: never hide every source on a parsing miss).
 */
export function selectDisplayedCitations(
  content: string,
  citations: Citation[]
): Array<{ citation: Citation; label: string }> {
  const labeled = labelCitations(citations);
  const cited = findCitedLabels(content);
  if (cited.size === 0) return labeled;
  const filtered = labeled.filter(({ label }) => cited.has(label));
  return filtered.length > 0 ? filtered : labeled;
}

export interface CitationGroup<T> {
  key: Bucket;
  heading: string;
  items: T[];
}

// Display order and human headings for the grouped Sources list.
const GROUP_ORDER: readonly Bucket[] = ['Web', 'Doc', 'M365'];
const GROUP_HEADING: Record<Bucket, string> = {
  Web: 'Web',
  Doc: 'AI Search',
  M365: 'M365',
};

/**
 * Group labeled citations by source bucket (parsed from the label prefix),
 * in a fixed display order, omitting empty groups. Item order within each
 * group is preserved. Generic over the item shape so callers can pass items
 * carrying extra fields (e.g. a resolved safe URL) alongside `label`.
 */
export function groupByLabel<T extends { label: string }>(
  items: T[]
): Array<CitationGroup<T>> {
  const byBucket = new Map<Bucket, T[]>();
  for (const item of items) {
    const bucket = item.label.split(' ')[0] as Bucket;
    const existing = byBucket.get(bucket);
    if (existing) existing.push(item);
    else byBucket.set(bucket, [item]);
  }
  return GROUP_ORDER.filter((b) => byBucket.has(b)).map((b) => ({
    key: b,
    heading: GROUP_HEADING[b],
    items: byBucket.get(b) as T[],
  }));
}
